import { ERRORS, getCurrentGeolocation } from "@pocketsign/in-app-sdk";
import { createFileRoute } from "@tanstack/react-router";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import type { FundSource } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import ChevronRight from "~icons/material-symbols/chevron-right";
import LocationOn from "~icons/material-symbols/location-on";
import Search from "~icons/material-symbols/search";
import Tune from "~icons/material-symbols/tune";
import { css } from "../../../../styled-system/css";
import Header from "../../../components/header";
import { IconImage } from "../../../components/icon-image";
import {
  SearchModal,
  StoreDetailModal,
  StoresSkeleton,
  selectButtonStyle,
  useStoresQuery,
} from "../../../features/stores";
import type {
  GeoLocation,
  Store,
  StoreDetail,
  StoreSearchFilters,
} from "../../../features/stores";
import {
  filterStoresBySelectedFundSources,
  getFundSourceAvailabilityMapForStore,
} from "../../../libs/fund-source-filters";
import { getSortedBalancesWithFundSources } from "../../../libs/get-sorted-balances-with-fund-sources";
import { t } from "../../../libs/i18n";
import {
  getCityAddressWithCache,
  reportSDKResult,
  sdk,
} from "../../../libs/sdk";
import { trimPrefecture } from "../../../libs/utils";

function AvailablePointIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M9.00131 16.125C8.01581 16.125 7.0895 15.938 6.22237 15.564C5.35525 15.19 4.601 14.6824 3.95962 14.0413C3.31825 13.4002 2.81044 12.6462 2.43619 11.7795C2.06206 10.9128 1.875 9.98669 1.875 9.00131C1.875 8.01581 2.062 7.0895 2.436 6.22237C2.81 5.35525 3.31756 4.601 3.95869 3.95962C4.59981 3.31825 5.35375 2.81044 6.2205 2.43619C7.08725 2.06206 8.01331 1.875 8.99869 1.875C9.98419 1.875 10.9105 2.062 11.7776 2.436C12.6448 2.81 13.399 3.31756 14.0404 3.95869C14.6818 4.59981 15.1896 5.35375 15.5638 6.2205C15.9379 7.08725 16.125 8.01331 16.125 8.99869C16.125 9.98419 15.938 10.9105 15.564 11.7776C15.19 12.6448 14.6824 13.399 14.0413 14.0404C13.4002 14.6818 12.6462 15.1896 11.7795 15.5638C10.9128 15.9379 9.98669 16.125 9.00131 16.125ZM9 15C10.675 15 12.0938 14.4188 13.2563 13.2563C14.4188 12.0938 15 10.675 15 9C15 7.325 14.4188 5.90625 13.2563 4.74375C12.0938 3.58125 10.675 3 9 3C7.325 3 5.90625 3.58125 4.74375 4.74375C3.58125 5.90625 3 7.325 3 9C3 10.675 3.58125 12.0938 4.74375 13.2563C5.90625 14.4188 7.325 15 9 15Z"
        fill="#001EFB"
      />
      <path
        d="M7.9211 12.4222C7.93816 12.6519 7.76258 12.858 7.53214 12.858H7.07445C6.84518 12.858 6.67073 12.654 6.68832 12.4255C6.70469 12.2127 6.711 11.9848 6.711 11.697V6.828C6.711 6.5638 6.70427 6.34 6.68669 6.11336C6.6686 5.88022 6.85385 5.67776 7.08758 5.68478C7.29215 5.69093 7.50327 5.694 7.782 5.694H9.663C10.536 5.694 11.148 5.901 11.58 6.333C11.949 6.693 12.138 7.233 12.138 7.881C12.138 8.565 11.931 9.123 11.535 9.537C11.094 9.978 10.482 10.185 9.618 10.185H8.274C8.06689 10.185 7.899 10.3529 7.899 10.56V11.688C7.899 11.9724 7.90517 12.2075 7.9211 12.4222ZM7.899 8.775C7.899 8.98211 8.06689 9.15 8.274 9.15H9.618C10.446 9.15 10.896 8.709 10.896 7.917C10.896 7.557 10.797 7.26 10.599 7.053C10.392 6.828 10.095 6.729 9.618 6.729H8.274C8.06689 6.729 7.899 6.89689 7.899 7.104V8.775Z"
        fill="#001EFB"
      />
    </svg>
  );
}

export const Route = createFileRoute("/_authed/stores/")({
  loader: async ({ context: { client, tenant } }) => {
    const [{ categories }, { cities }, balanceResponse] = await Promise.all([
      client.getCategories({}),
      client.getAllCities({}),
      client.getMyBalances({}),
    ]);

    // getAllCities() のバックエンド由来の市区町村名と突き合わせるキーなので翻訳しない
    const defaultCurrentCity =
      tenant.slug === "marumori"
        ? "宮城県伊具郡丸森町"
        : tenant.slug === "hokkaido"
          ? "北海道札幌市中央区"
          : tenant.slug === "kyoto-kyoto"
            ? "京都府京都市中京区"
            : tenant.slug === "shiga-otsu-pay"
              ? "滋賀県大津市"
              : "宮城県仙台市青葉区";

    const cityAddressResource = await getCityAddressWithCache();

    let defaultCity = defaultCurrentCity;

    if (cityAddressResource && cities.includes(cityAddressResource)) {
      defaultCity = cityAddressResource;
    }

    const { sortedBalances, filtersByFundSourceId } =
      await getSortedBalancesWithFundSources(
        client,
        balanceResponse.balanceWithFundSources,
      );

    const ownedBalances = sortedBalances.filter(
      (balance) =>
        balance.userBalance?.amount && balance.userBalance.amount > 0,
    );
    const availableFundSources = ownedBalances
      .map((balance) => balance.fundSource)
      .filter((fundSource): fundSource is FundSource => Boolean(fundSource));

    return {
      categories,
      defaultCity,
      cities,
      availableFundSources,
      filtersByFundSourceId,
      client,
      tenant,
    };
  },

  component: Stores,
});

function Stores() {
  const {
    categories,
    cities,
    defaultCity,
    tenant,
    client,
    availableFundSources,
    filtersByFundSourceId,
  } = Route.useLoaderData();
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [shouldFocusInput, setShouldFocusInput] = useState(false);
  const [selectedCities, setSelectedCities] = useState<string[]>([defaultCity]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedFundSourceIds, setSelectedFundSourceIds] = useState<string[]>(
    [],
  );
  const [searchText, setSearchText] = useState("");
  const [isStoreDetailModalOpen, setIsStoreDetailModalOpen] = useState(false);
  const [storeDetail, setStoreDetail] = useState<StoreDetail | null>(null);
  const [isStoreDetailLoading, setIsStoreDetailLoading] = useState(false);
  const [location, setLocation] = useState<GeoLocation | undefined>(undefined);

  const storesQuery = useStoresQuery({
    client,
    query: {
      name: searchText,
      cities: selectedCities,
      categories: selectedCategories.length > 0 ? selectedCategories : [],
    },
    location,
    cities,
    categories,
  });

  const stores = useMemo(
    () =>
      filterStoresBySelectedFundSources({
        stores: storesQuery.data || [],
        selectedFundSourceIds,
        filtersByFundSourceId,
      }),
    [filtersByFundSourceId, selectedFundSourceIds, storesQuery.data],
  );
  const loading = storesQuery.isLoading;

  useEffect(() => {
    try {
      const result = getCurrentGeolocation(sdk, {
        enableHighAccuracy: false,
      });
      void reportSDKResult("getCurrentGeolocation", () => result, {
        ignoredErrnos: [
          ERRORS.APP_PERMISSION_DENIED,
          ERRORS.DEVICE_SETTINGS_NOT_SATISFIED,
        ],
      }).then((response) => {
        if (response.result === "success") {
          setLocation({
            latitude: response.coords.latitude,
            longitude: response.coords.longitude,
          });
        }
      });
    } catch (e) {
      console.error(e);
    }
  }, []);

  const handleClickCategory = useCallback(
    (category: string) => {
      const newSelectedCategories = selectedCategories.includes(category)
        ? selectedCategories.filter((c) => c !== category)
        : [...selectedCategories, category];
      setSelectedCategories(newSelectedCategories);
    },
    [selectedCategories],
  );

  const openSearchModal = (focusInput: boolean) => {
    if (tenant.feature.storeList) {
      setIsSearchModalOpen(true);
      setShouldFocusInput(focusInput);
    }
  };

  const selectedCityText = useMemo(() => {
    if (selectedCities.length === 0) {
      return t("未選択");
    }
    if (selectedCities.length === 1) {
      return trimPrefecture(selectedCities[0]);
    }

    return t("複数地域");
  }, [selectedCities]);

  const handleSearch = useCallback((result: StoreSearchFilters) => {
    setIsSearchModalOpen(false);
    setSearchText(result.text);
    setSelectedFundSourceIds(result.selectedFundSourceIds);
    if (result.text.length === 0) {
      setSelectedCities(result.cities);
      setSelectedCategories(result.categories);
    }
  }, []);

  const openStoreDetailModal = useCallback(
    async (storeId: string) => {
      setIsStoreDetailLoading(true);
      try {
        const storeResponse = await client.getStore({ storeId });
        if (!storeResponse.store) {
          toast.error(t("店舗が見つかりません"));
          setIsStoreDetailModalOpen(false);
          return;
        }
        const { availableFundSources: storeAvailableFundSources } =
          getFundSourceAvailabilityMapForStore({
            store: storeResponse.store,
            fundSources: availableFundSources,
            filtersByFundSourceId,
          });
        setStoreDetail({
          store: storeResponse.store,
          availableFundSources: storeAvailableFundSources,
        });
        setIsStoreDetailModalOpen(true);
      } finally {
        setIsStoreDetailLoading(false);
      }
    },
    [availableFundSources, client, filtersByFundSourceId],
  );

  return (
    <>
      <Header title={t("取り扱い店舗")} />

      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          pt: "16px",
          pb: "48px",
        })}
      >
        {!tenant.feature.storeList && (
          <div
            className={css({
              display: "flex",
              gap: "16px",
              px: "16px",
            })}
          >
            <button
              type="button"
              className={css({
                display: "flex",
                alignItems: "center",
                gap: "8px",
                bg: "surface.disable",
                rounded: "xl",
                py: "8px",
                px: "16px",
                flexGrow: 1,
                color: "text.disable",
                border: "1px solid",
                borderColor: "border.disable",
              })}
              onClick={() => openSearchModal(true)}
            >
              <Search
                className={css({
                  width: "20px",
                  height: "20px",
                })}
              />
              <span>{t("店舗名で探す")}</span>
            </button>
          </div>
        )}
        {tenant.feature.storeList && (
          <div
            className={css({
              display: "flex",
              gap: "16px",
              px: "16px",
            })}
          >
            <button
              type="button"
              className={css({
                display: "flex",
                alignItems: "center",
                gap: "8px",
                bg: "white",
                rounded: "xl",
                py: "8px",
                px: "16px",
                flexGrow: 1,
                color: "text.secondary",
                border: "1px solid",
                borderColor: "border.secondary",
              })}
              onClick={() => openSearchModal(true)}
            >
              <Search
                className={css({
                  width: "20px",
                  height: "20px",
                })}
              />
              <span>
                {searchText.length > 0 ? searchText : t("店舗名で探す")}
              </span>
            </button>

            <button
              type="button"
              className={css({
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "text.secondary",
              })}
              onClick={() => openSearchModal(false)}
            >
              <Tune
                className={css({
                  width: "24px",
                  height: "24px",
                })}
              />
            </button>
          </div>
        )}

        <div
          className={css({
            display: "flex",
            gap: "8px",
            px: "16px",
            overflowX: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          })}
        >
          {cities.length > 1 && (
            <button
              type="button"
              className={clsx(css(selectButtonStyle), "active")}
              onClick={() => openSearchModal(false)}
            >
              <LocationOn />
              <span>
                {searchText.length > 0 ? t("全て") : selectedCityText}
              </span>
            </button>
          )}

          {selectedFundSourceIds.length > 0 && (
            <button
              type="button"
              className={clsx(css(selectButtonStyle), "active")}
              onClick={() => openSearchModal(false)}
            >
              <AvailablePointIcon />
              <span>{t("利用可能ポイント")}</span>
            </button>
          )}

          {searchText.length === 0 &&
            categories.map((category) => (
              <button
                type="button"
                className={clsx(
                  css(selectButtonStyle),
                  selectedCategories.includes(category) && "active",
                )}
                key={category}
                onClick={() => handleClickCategory(category)}
              >
                <span>{category}</span>
              </button>
            ))}
        </div>

        <div
          className={css({
            px: "16px",
            fontSize: "14px",
            color: "text.secondary",
          })}
        >
          {t("全 {count} 件", { count: stores.length })}
        </div>

        {!tenant.feature.storeList && (
          <div
            className={css({
              color: "text.secondary",
              textAlign: "center",
              py: "16px",
            })}
          >
            <p>{t("取り扱い店舗はありません")}</p>
          </div>
        )}

        {tenant.feature.storeList && !loading && stores.length === 0 && (
          <div
            className={css({
              color: "text.secondary",
              textAlign: "center",
              py: "16px",
            })}
          >
            <p>{t("条件に合う取り扱い店舗はありません")}</p>
          </div>
        )}

        {tenant.feature.storeList && loading ? (
          <StoresSkeleton />
        ) : (
          tenant.feature.storeList &&
          stores.length > 0 && (
            <div className={css({ px: "16px", maxWidth: "100%" })}>
              <div
                className={css({
                  bg: "white",
                  rounded: "xl",
                  maxWidth: "100%",
                })}
              >
                {stores.map((store: Store, index: number) => (
                  <button
                    type="button"
                    onClick={() => openStoreDetailModal(store.id)}
                    key={store.id}
                    className={css({
                      display: "flex",
                      alignItems: "center",
                      pl: "16px",
                      maxWidth: "100%",
                      gap: "12px",
                      width: "full",
                      textAlign: "left",
                    })}
                  >
                    <div
                      className={css({
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      })}
                    >
                      <IconImage
                        src={store.iconUrl}
                        alt={store.name}
                        size={42}
                      />
                    </div>
                    <div
                      className={css({
                        flexGrow: 1,
                        display: "flex",
                        borderBottom:
                          index === stores.length - 1 ? "none" : "1px solid",
                        py: "16px",
                        maxWidth: "calc(100% - 48px)",
                        borderColor: "border.secondary",
                      })}
                    >
                      <div
                        className={css({
                          display: "flex",
                          flexGrow: 1,
                          minWidth: 0,
                          flexDirection: "column",
                          width: "full",
                        })}
                      >
                        <h3
                          className={css({
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          })}
                        >
                          {store.name}
                        </h3>
                        <p
                          className={css({
                            color: "text.tertiary",
                            fontSize: "12px",
                          })}
                        >
                          {store.category}
                        </p>
                      </div>

                      <div
                        className={css({
                          color: "text.tertiary",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          pr: "16px",
                          flexShrink: 0,
                        })}
                      >
                        <ChevronRight
                          className={css({ marginLeft: "8px", color: "#999" })}
                        />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )
        )}
      </div>

      <SearchModal
        cities={cities}
        defaultCity={defaultCity}
        categories={categories}
        availableFundSources={availableFundSources}
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onSearch={handleSearch}
        shouldFocusInput={shouldFocusInput}
        selectedCities={selectedCities}
        selectedCategories={selectedCategories}
        selectedFundSourceIds={selectedFundSourceIds}
      />
      <StoreDetailModal
        isOpen={isStoreDetailModalOpen}
        onClose={() => setIsStoreDetailModalOpen(false)}
        detail={storeDetail}
      />

      {isStoreDetailLoading && (
        <div
          className={css({
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          })}
        >
          <div
            className={css({
              width: "40px",
              height: "40px",
              border: "4px solid",
              borderColor: "border.secondary",
              borderTopColor: "surface.accentPrimary",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            })}
          />
        </div>
      )}
    </>
  );
}
